/**
 * Detalle de un producto (ADM-02): todo lo que el backoffice sabe de el, en una
 * sola pantalla, y todo editable desde aca.
 *
 * La base del sitio es la fuente de verdad del catalogo —no hay un ERP detras—,
 * asi que el nombre, el codigo y el precio se cambian aca. La excepcion es el
 * STOCK: no tiene un campo para escribirlo, se cambia anotando un movimiento
 * (`PanelStockProducto`), que es lo que deja el rastro.
 *
 * "Volver" apunta a la lista con la pagina, la busqueda y los filtros que tenia
 * cuando se hizo clic (llega por `location.state`, lo arma `Productos.tsx`).
 *
 * **Eliminar.** A diferencia de ocultar (reversible con el mismo interruptor),
 * esto borra la fila: pasa por una confirmacion en modal que nombra el producto.
 * Es seguro —los pedidos y los movimientos de inventario guardan su propia copia
 * del producto— y esta razonado en `eliminarProductoAdmin()` (backend).
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import type { ActualizarSeoDto, ProductoAdminDetalle as Detalle } from '@bandu/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { FormularioContenido } from '../../componentes/administracion/FormularioContenido';
import {
  FormularioProducto,
  type ValoresProducto,
} from '../../componentes/administracion/FormularioProducto';
import { FormularioSeo } from '../../componentes/administracion/FormularioSeo';
import { GaleriaProductoAdmin } from '../../componentes/administracion/GaleriaProductoAdmin';
import { Modal } from '../../componentes/administracion/Modal';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { PanelStockProducto } from '../../componentes/administracion/PanelStockProducto';
import { VideoProductoAdmin } from '../../componentes/administracion/VideoProductoAdmin';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import {
  actualizarProducto,
  actualizarSeoProducto,
  actualizarVisibilidadProducto,
  eliminarProducto,
  obtenerProductoAdmin,
} from '../../servicios/administracion.servicio';
import { destacarProducto, obtenerCategoriasAdmin } from '../../servicios/portada.servicio';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { nombreConVariante } from '../../utilidades/producto';
import { rutas } from '../../utilidades/rutas';
import type { BaseDeVariante } from './ProductoNuevo';

export function ProductoDetalle() {
  const { id: idTexto } = useParams<{ id: string }>();
  const id = Number(idTexto);
  const ubicacion = useLocation();
  const navegar = useNavigate();
  const volver =
    (ubicacion.state as { volver?: string } | null)?.volver ?? rutas.administracion.productos();

  // Guardar los datos puede cambiar cosas que calcula el servidor (el grupo y sus
  // variantes): despues de guardar se vuelve a pedir el producto entero.
  const [version, setVersion] = useState(0);
  const peticion = usePeticion((senal) => obtenerProductoAdmin(id, senal), [id, version]);
  const categorias = usePeticion((senal) => obtenerCategoriasAdmin(senal), []);

  const [producto, setProducto] = useState<Detalle | null>(null);
  const [falla, setFalla] = useState<string | null>(null);
  // Cuenta las veces que llego el producto desde el servidor. Es la `key` del
  // formulario de datos: tiene que rearmarse cuando LLEGA lo guardado, no cuando
  // se pide —en ese instante todavia tendria los valores viejos.
  const [llegadas, setLlegadas] = useState(0);

  useEffect(() => {
    if (peticion.datos === null) return;
    setProducto(peticion.datos);
    setLlegadas((actual) => actual + 1);
  }, [peticion.datos]);

  const [guardandoDatos, setGuardandoDatos] = useState(false);
  const [datosGuardados, setDatosGuardados] = useState(false);
  const [fallaDatos, setFallaDatos] = useState<string | null>(null);

  const [editandoSeo, setEditandoSeo] = useState(false);
  const [enviandoSeo, setEnviandoSeo] = useState(false);
  const [fallaSeo, setFallaSeo] = useState<string | null>(null);

  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [fallaEliminar, setFallaEliminar] = useState<string | null>(null);

  async function guardarDatos(valores: ValoresProducto): Promise<void> {
    if (producto === null) return;
    setGuardandoDatos(true);
    setFallaDatos(null);
    setDatosGuardados(false);
    try {
      // El stock inicial es solo del alta: aca no viaja.
      const { stockInicial: _sinUso, ...datos } = valores;
      await actualizarProducto(producto.id, datos);
      setDatosGuardados(true);
      setVersion((actual) => actual + 1);
    } catch (fallo) {
      setFallaDatos(mensajeDe(fallo));
    } finally {
      setGuardandoDatos(false);
    }
  }

  async function alternarVisible(visible: boolean): Promise<void> {
    if (producto === null) return;
    setFalla(null);
    try {
      await actualizarVisibilidadProducto(producto.id, visible);
      setProducto({ ...producto, visible });
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    }
  }

  async function alternarDestacado(destacado: boolean): Promise<void> {
    if (producto === null) return;
    setFalla(null);
    try {
      await destacarProducto(producto.id, destacado);
      setProducto({ ...producto, destacado });
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    }
  }

  async function guardarSeo(datos: ActualizarSeoDto): Promise<void> {
    if (producto === null) return;
    setFallaSeo(null);
    setEnviandoSeo(true);
    try {
      await actualizarSeoProducto(producto.id, datos);
      setProducto({ ...producto, ...datos });
      setEditandoSeo(false);
    } catch (fallo) {
      setFallaSeo(mensajeDe(fallo));
    } finally {
      setEnviandoSeo(false);
    }
  }

  async function eliminar(): Promise<void> {
    if (producto === null) return;
    setEliminando(true);
    setFallaEliminar(null);
    try {
      await eliminarProducto(producto.id);
      navegar(volver);
    } catch (fallo) {
      setFallaEliminar(mensajeDe(fallo));
      setEliminando(false);
    }
  }

  if (peticion.estado === 'cargando' && producto === null) return <Cargando que="el producto" />;
  if (peticion.estado === 'error' && producto === null) return <AvisoError error={peticion.error} />;
  if (producto === null) return null;

  const nombreCompleto = nombreConVariante(producto);

  // Lo que hereda una talla o un color nuevo de este producto.
  const baseDeVariante: BaseDeVariante = {
    nombre: producto.nombre,
    marca: producto.marca,
    categoriaId: producto.categoriaId,
    precio: producto.precio,
    precioAnterior: producto.precioAnterior,
    unidadMedida: producto.unidadMedida,
    grupoVariante: producto.grupoVariante,
    ordenVariante: producto.variantes.length,
  };

  return (
    <>
      <Seo
        titulo={nombreCompleto}
        descripcion={`Detalle de ${nombreCompleto} en el backoffice.`}
        ruta={rutas.administracion.productoDetalle(producto.id)}
        sinIndexar
      />

      <BarraAdmin
        titulo={nombreCompleto}
        bajada={producto.sku}
        acciones={
          <Link className="btn btn-sm btn-contorno" to={volver}>
            <Icono nombre="flechaIzq" />
            Volver
          </Link>
        }
      />

      <div className="admin-cuerpo">
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            {falla}
          </p>
        ) : null}

        <GaleriaProductoAdmin
          productoId={producto.id}
          nombreProducto={nombreCompleto}
          imagenes={producto.imagenes}
          onCambio={(imagenes) => {
            setProducto({ ...producto, imagenes });
          }}
        />

        <VideoProductoAdmin
          productoId={producto.id}
          videos={producto.videos}
          onCambio={(videos) => {
            setProducto({ ...producto, videos });
          }}
        />

        <div className="panel-doble">
          <div>
            <PanelAdmin titulo="Datos del producto">
              {categorias.datos !== null ? (
                <FormularioProducto
                  // Con `key`: despues de guardar, el formulario arranca con lo que quedo en el servidor.
                  key={llegadas}
                  inicial={{
                    sku: producto.sku,
                    nombre: producto.nombre,
                    marca: producto.marca,
                    categoriaId: producto.categoriaId,
                    precio: producto.precio,
                    precioAnterior: producto.precioAnterior,
                    unidadMedida: producto.unidadMedida,
                    grupoVariante: producto.grupoVariante,
                    etiquetaVariante: producto.etiquetaVariante,
                    ordenVariante: producto.ordenVariante,
                    stockInicial: 0,
                  }}
                  categorias={categorias.datos}
                  gruposExistentes={producto.grupoVariante !== null ? [producto.grupoVariante] : []}
                  conStockInicial={false}
                  textoBoton="Guardar datos"
                  enviando={guardandoDatos}
                  falla={fallaDatos}
                  guardado={datosGuardados}
                  onGuardar={(valores) => {
                    void guardarDatos(valores);
                  }}
                />
              ) : (
                <Cargando que="las categorias" />
              )}
            </PanelAdmin>

            <PanelAdmin
              titulo="Descripción y ficha técnica"
              bajada="Lo que lee el comprador en la ficha, y lo que lee Google."
            >
              <FormularioContenido
                productoId={producto.id}
                inicial={{ descripcion: producto.descripcion, fichaTecnica: producto.fichaTecnica }}
                onGuardado={(datos) => {
                  setProducto({ ...producto, ...datos });
                }}
              />
            </PanelAdmin>
          </div>

          <div>
            <PanelStockProducto
              producto={{
                id: producto.id,
                nombre: nombreCompleto,
                stock: producto.stock,
                precio: producto.precio,
              }}
              onStockCambiado={(stock) => {
                setProducto({
                  ...producto,
                  stock,
                  estadoStock: stock > 0 ? 'disponible' : 'agotado',
                });
              }}
            />

            <PanelAdmin
              titulo="Tallas, colores y medidas"
              bajada={
                producto.grupoVariante !== null
                  ? 'Se ofrecen juntas en la ficha, con un selector.'
                  : 'Este producto no tiene variantes.'
              }
              extra={
                producto.grupoVariante !== null ? (
                  <Link
                    className="btn btn-sm btn-contorno"
                    to={rutas.administracion.productoNuevo()}
                    state={{ base: baseDeVariante }}
                  >
                    Crear otra variante
                  </Link>
                ) : null
              }
            >
              {producto.variantes.length > 0 ? (
                <ul className="lista-variantes">
                  {producto.variantes.map((variante) => (
                    <li key={variante.id}>
                      <Link
                        className={variante.id === producto.id ? 'etq etq-info' : 'etq etq-neutra'}
                        to={rutas.administracion.productoDetalle(variante.id)}
                        aria-current={variante.id === producto.id ? 'true' : undefined}
                      >
                        {variante.etiqueta}
                        {variante.estadoStock === 'agotado' ? ' · agotada' : ''}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="parrafo-panel">
                  Para ofrecerlo en varias tallas o colores, escribe un grupo en sus datos y crea
                  las demás variantes con ese mismo grupo.
                </p>
              )}
            </PanelAdmin>

            <PanelAdmin titulo="Visibilidad">
              {/*
                El interruptor dice "Publicado", pero la tienda solo muestra lo
                que ademas tiene precio (`utilidades/publicacion.ts` en el
                backend). Sin este aviso, el producto parece roto.
              */}
              {producto.visible && producto.precio <= 0 ? (
                <p className="acceso-error" role="status">
                  <Icono nombre="alerta" />
                  <span>
                    Tiene el precio en cero, así que la tienda no lo muestra aunque esté publicado.
                    Se ve apenas le pongas precio.
                  </span>
                </p>
              ) : null}
              <div className="lista-interruptores">
                <button
                  type="button"
                  className="interruptor-boton"
                  aria-pressed={producto.visible}
                  onClick={() => {
                    void alternarVisible(!producto.visible);
                  }}
                >
                  <span className={producto.visible ? 'interruptor on' : 'interruptor'} />
                  <span>{producto.visible ? 'Publicado' : 'Oculto'}</span>
                </button>

                <button
                  type="button"
                  className="interruptor-boton"
                  aria-pressed={producto.destacado}
                  disabled={!producto.visible}
                  title={producto.visible ? undefined : 'Un producto oculto no aparece en la portada'}
                  onClick={() => {
                    void alternarDestacado(!producto.destacado);
                  }}
                >
                  <span className={producto.destacado ? 'interruptor on' : 'interruptor'} />
                  <span>{producto.destacado ? 'Destacado en portada' : 'No destacado'}</span>
                </button>
              </div>
            </PanelAdmin>

            <PanelAdmin
              titulo="SEO"
              extra={
                <button
                  type="button"
                  className="btn btn-sm btn-contorno"
                  onClick={() => {
                    setFallaSeo(null);
                    setEditandoSeo(true);
                  }}
                >
                  Editar
                </button>
              }
            >
              <ul className="lista-cifras">
                <li>
                  <span>Título</span>
                  <span>{producto.tituloSeo ?? 'Se genera solo'}</span>
                </li>
                <li>
                  <span>Descripción</span>
                  <span>{producto.descripcionSeo ?? 'Se genera sola'}</span>
                </li>
              </ul>
            </PanelAdmin>

            <PanelAdmin titulo="Eliminar producto">
              <p className="parrafo-panel">
                Borra el producto para siempre. Si solo quieres que deje de verse en la tienda,
                ocúltalo en vez de eliminarlo: eso se puede deshacer.
              </p>
              <button
                type="button"
                className="btn btn-sm btn-peligro"
                onClick={() => {
                  setFallaEliminar(null);
                  setConfirmandoEliminar(true);
                }}
              >
                Eliminar producto
              </button>
            </PanelAdmin>
          </div>
        </div>
      </div>

      <FormularioSeo
        entidad={editandoSeo ? producto : null}
        titulo={`SEO de ${nombreCompleto}`}
        bajada="Titulo y descripcion del resultado en buscadores"
        placeholderTitulo={nombreCompleto}
        placeholderDescripcion="Se genera sola si se deja vacio"
        enviando={enviandoSeo}
        falla={fallaSeo}
        onCerrar={() => {
          setEditandoSeo(false);
        }}
        onGuardar={(datos) => {
          void guardarSeo(datos);
        }}
      />

      {/*
        Eliminar no tiene vuelta atras, asi que pasa por una confirmacion que
        nombra el producto. No se usa el `confirm()` del navegador: bloquea la
        pestaña entera y no se puede redactar (mismo patron que Administradores.tsx).
      */}
      <Modal
        titulo="Eliminar este producto"
        bajada="No se puede deshacer"
        abierto={confirmandoEliminar}
        onCerrar={() => {
          if (!eliminando) setConfirmandoEliminar(false);
        }}
      >
        {fallaEliminar !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{fallaEliminar}</span>
          </p>
        ) : null}

        <p className="parrafo-panel">
          <strong>{nombreCompleto}</strong> ({producto.sku}) se borra para siempre, con sus fotos.
          Su historial de inventario y los pedidos donde aparece se conservan.
        </p>

        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-contorno"
            onClick={() => {
              setConfirmandoEliminar(false);
            }}
            disabled={eliminando}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-peligro"
            onClick={() => {
              void eliminar();
            }}
            disabled={eliminando}
          >
            {eliminando ? 'Eliminando…' : 'Eliminar el producto'}
          </button>
        </div>
      </Modal>
    </>
  );
}
